import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  FiltrosCompras,
  IdentificacaoDeOficina,
  SEM_CENTRO_CUSTO,
} from '../../../api/comprasDashboardApi';
import { useComprasDashboard } from '../hooks/useComprasDashboard';
import { blocosDoModo } from '../blocosDoModo';
import CarimboSync from '../components/CarimboSync';
import FaixaEstadoDaSincronizacao from '../components/FaixaEstadoDaSincronizacao';
import FiltroComprasModal from '../components/FiltroComprasModal';
import PainelSincronizacao from '../components/PainelSincronizacao';
import RequisicoesDoRecorteModal, {
  RecorteAberto,
} from '../components/RequisicoesDoRecorteModal';
import PedidosDoMesModal from '../components/PedidosDoMesModal';
import PedidosDoFornecedorModal, {
  FornecedorAberto,
} from '../components/PedidosDoFornecedorModal';
import ItensDoCompradorModal, { ItensAbertos } from '../components/ItensDoCompradorModal';
import PedidosDoRecorteModal, { PedidosDoRecorteAberto } from '../components/PedidosDoRecorteModal';
import {
  CargaCompradores,
  FilaPorCompradorBloco,
  ItensMaisRequisitadosBloco,
  KpisCompras,
  Oficinas,
  RequisicoesPorPlacaBloco,
  RequisicoesPorCentroCustoBloco,
  RequisicoesPorSolicitanteBloco,
  UltimasRequisicoesBloco,
} from '../components/BlocosCompras';
import AbaPedidos from '../components/BlocosPedidos';
import { rotuloDosStatus } from '../situacoes';
import { formatarDataPura } from '../formatarData';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import '../../../components/como-funciona/help-circle-btn.css';
// Import explícito do casco: contar com o CSS carregado por outra tela é acoplamento invisível
// (BUG-V74).
import '../../recepcao/pages/Recepcao.css';
import '../../../shared/ui/dashboard-base.css';
import '../../../shared/ui/badge-status.css';
import '../../../shared/feedback/erro-de-tela.css';
import './DashboardCompras.css';
import DetalheDoPedidoModal from '../components/DetalheDoPedidoModal';
import DetalheDaRequisicaoModal from '../components/DetalheDaRequisicaoModal';
import '../../../shared/feedback/aviso-de-tela.css';

/**
 * Dashboard de Compras (at-100, ADR 0051) — ADMIN e COMPRA.
 *
 * ⚠️ Os números vêm de um ESPELHO do Nasajon mantido pelo SOAC, não da API do ERP a cada abertura:
 * a fonte não tem contagem nem ordenação, e os pedidos custam dezenas de segundos por página. O
 * espelho é atualizado a cada 3 minutos no que muda (era 1 min até a at-99), e a tela se refaz sozinha quando o
 * servidor avisa que houve mudança — por isso o carimbo "Atualizado há X" é parte do conteúdo, e
 * não enfeite.
 *
 * 🔴 Os dois indicadores que o ERP não entrega:
 *
 * **Tempo de atendimento** — saiu da tela na at-116 (decisão do Victor, 27/09/2026). O Nasajon não
 * guarda quando a requisição mudou de situação, então o número só podia ser construído pela
 * observação do próprio SOAC — e, com o sync parado, ele sai maior do que foi, sem nada denunciando.
 * O cálculo continua no backend, sem consumidor. ⚠️ **No lugar do bloco de espera, desde a at-119,
 * está "Tempo na fila por comprador"** (`FilaPorCompradorBloco`): OUTRA métrica — a do KPI "Tempo
 * na fila de Compras" (`hoje − data_cadastro` do ERP, só do que ainda está na fila), agrupada por
 * pessoa. Ela não depende de observação nenhuma, e é por isso que pôde entrar sem a Nasajon.
 *
 * **Entrega no prazo** — sem dados de 22/09 a 19/10/2026 (a `previsao` da nota fiscal é a própria
 * data da chegada). De 19 a 22/10/2026 lia o relatório subido à mão; desde a at-128 lê o elo
 * pedido → nota da API do Nasajon: previsão do PEDIDO × entrada da última NOTA. Cobre **somente a
 * oficina interna** (a peça externa é avisada por WhatsApp e não gera nota). **Vive na aba Pedidos
 * desde a at-119.**
 *
 * ⚠️ O tempo de atendimento continua sem fonte: a Nasajon respondeu em 21/10/2026 que a data de
 * mudança de situação não existe no fluxo do ERP.
 */

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'De onde vêm os números',
    conteudo: (
      <>
        <p>
          Do <strong>Nasajon</strong>, por uma cópia que o sistema atualiza sozinho. O que muda com
          frequência — requisições pendentes, em cotação — é lido <strong>a cada 3 minutos</strong>; os
          pedidos de compra, <strong>de hora em hora</strong> nos últimos dias, e a releitura
          completa deles <strong>uma vez por semana</strong>, porque aquela consulta é lenta no ERP.
        </p>
        <p>
          As leituras frequentes acontecem <strong>de segunda a sexta, das 8h às 19h</strong>. Fora
          desse horário elas ficam paradas de propósito: nos últimos seis meses, o fim de semana
          inteiro concentrou pouco mais de <strong>1% das requisições</strong>, e cada leitura tem
          custo no ERP. As leituras de madrugada continuam fora desse horário: entregas e negociações
          todo dia, e a releitura completa dos pedidos e os cadastros aos domingos.
        </p>
        <p>
          O aviso <strong>Atualizado há</strong>, no topo, diz quando cada parte foi lida pela
          última vez. Dentro do horário acima, quando alguém muda alguma coisa no Nasajon esta tela
          se atualiza sozinha em até 3 minutos — não é preciso recarregar.
        </p>
      </>
    ),
  },
  {
    titulo: 'O que cada situação significa',
    conteudo: (
      <>
        <p>
          <strong>Antes de Compras</strong> é o que ainda não passou pelo aprovador: requisição em
          aprovação, com aprovação não iniciada ou reprovada. Enquanto está assim, ela{' '}
          <strong>não é fila do setor de Compras</strong> — está esperando quem aprova, não quem
          compra —, e por isso não entra em nenhum dos indicadores de fila nem no tempo de espera.
        </p>
        <p>
          <strong>Aguardando atendimento</strong> é a requisição já aprovada que ainda não tem
          comprador designado. <strong>Em atendimento</strong> é a que já tem. As duas estão como
          "pendente" no ERP — a separação é feita aqui, olhando se existe comprador.
        </p>
        <p>
          Os cards das etapas — de <strong>Antes de Compras</strong> a{' '}
          <strong>Canceladas</strong> — <strong>não se sobrepõem</strong>: cada requisição aparece
          em um só, e somá-los dá o total do período.
        </p>
        <p>
          <strong>Atendida</strong> é o que o ERP marcou como atendida. Isso não é o mesmo que a
          peça ter chegado — o ERP não registra a chegada de forma que o sistema consiga ler.
        </p>
      </>
    ),
  },
  {
    titulo: 'Os filtros do topo valem para a tela inteira',
    conteudo: (
      <>
        <p>
          O que você escolher em <strong>Filtros</strong> — período, centro de custo, comprador —
          vale para <strong>todos os blocos</strong>, inclusive os que contam pedidos de compra.
          Enquanto o painel busca os números novos, os indicadores mostram um girador no lugar do
          valor: o número que estava na tela é do filtro anterior, e ele sai para não ser lido como
          resposta.
        </p>
        <p>
          <strong>O período corta pela data de cadastro da requisição.</strong> Isso vale também
          para as filas: uma requisição parada desde agosto não aparece quando o período escolhido
          é setembro. Para encontrá-la, alargue o período.
        </p>
        <p>
          Quando um filtro <strong>não pode</strong> ser aplicado a um bloco, ele diz isso no
          próprio bloco, em vez de mostrar um número que ignorou a sua escolha. Em{' '}
          <strong>Entrega no prazo</strong>, o comprador e o centro de custo valem pela requisição
          que originou cada pedido; o pedido sem requisição de origem (compra direta) fica fora
          do recorte, e o bloco diz quantos são.
        </p>
        <p>
          Filtrando por um comprador, <strong>Aguardando atendimento</strong> fica sem número — e
          explica por quê. Aquele indicador conta justamente as requisições que{' '}
          <em>ainda não têm</em> comprador, então não há o que contar. Não quer dizer que a fila
          esteja vazia. Pelo mesmo motivo, <strong>Carga por comprador</strong> deixa de ser o
          gráfico comparativo e passa a mostrar os números daquela pessoa.
        </p>
      </>
    ),
  },
  {
    titulo: 'Por que existe a linha "fora do período"',
    conteudo: (
      <p>
        Há requisições com pedido gerado há mais de um ano que nunca foram baixadas no ERP. Elas não
        são fila de trabalho — se entrassem na conta, o painel mostraria milhares de itens
        "aguardando entrega". Por isso o painel conta apenas as do período escolhido e informa o
        restante em uma linha separada, como pendência de organização do cadastro.
      </p>
    ),
  },
  {
    titulo: 'Entrega no prazo: de onde vem e o que cobre',
    conteudo: (
      <>
        <p>
          O bloco fica na aba <strong>Pedidos</strong>, ao lado de Fornecedores e da Evolução
          mensal: ele fala de pedido de compra, não de requisição.
        </p>
        <p>
          Ele compara a <strong>previsão de entrega registrada no pedido</strong> com a{' '}
          <strong>data de entrada da nota fiscal</strong> que o atendeu. As duas vêm direto do
          Nasajon, que liga cada pedido às notas de entrada dele. Quando o pedido chega em mais de
          uma nota, ele conta como entregue na data da <strong>última</strong>: entrega parcial não
          é entrega.
        </p>
        <p>
          A chegada é lida do Nasajon <strong>de hora em hora</strong>, no horário de expediente, e
          todo dia de madrugada. O bloco mostra até que dia vão as entradas e quando foi a última
          leitura, e avisa quando ela não acontece há mais de 2 dias. A mesma chegada aparece, pedido
          a pedido, na coluna <strong>Chegada</strong> da lista: no prazo, atrasado, aguardando ou
          previsão vencida.
        </p>
        <p>
          O card <strong>Acompanhamento de entrega</strong> filtra por esses mesmos estados — pode
          marcar mais de um, e sem nenhum marcado mostra todos. Ele abre em{' '}
          <strong>Aguardando</strong>: os pedidos ainda sem nota de entrada cuja previsão não passou.
          Os de previsão já passada ficam em <strong>Previsão vencida</strong>, separados.
        </p>
        <p>
          <strong>Previsão vencida não quer dizer atraso.</strong> Significa que o prazo registrado
          passou e não há nota de entrada — o pedido pode ter chegado direto na oficina externa.
        </p>
        <p>
          <strong>A peça que vai direto para oficina externa não entra nessa conta.</strong> Ela é
          avisada em grupo de WhatsApp e nunca é registrada no Nasajon, então não existe data de
          chegada para comparar. Isso não é limitação do painel: o dado não é registrado em sistema
          nenhum, e nenhuma mudança de software resolveria.
        </p>
        <p>
          O bloco informa <strong>quantos pedidos puderam ser medidos</strong> e quantos ficaram
          de fora: sem previsão, com previsão anterior à emissão do pedido, ou com entrada da nota
          anterior à emissão do pedido. Os dois últimos são erro de preenchimento no ERP e
          apareceriam como atraso ou adiantamento falsos. Pedidos com previsão no{' '}
          <strong>próprio dia da emissão</strong> são contados normalmente, e o bloco diz quantos
          são.
        </p>
        <p>
          O bloco obedece ao filtro de <strong>comprador</strong> e de <strong>centro de custo</strong>,
          pela requisição que originou cada pedido. Ele só aparece quando o recorte inclui algum
          centro de oficina, porque o assunto é peça.
        </p>
      </>
    ),
  },
  {
    titulo: 'O filtro de centro de custo, e como ele muda a tela',
    conteudo: (
      <>
        <p>
          O painel é recortado por <strong>centro de custo</strong> — quem paga a compra. Ele não
          está na requisição: vem do <strong>local de uso</strong> escolhido por quem a cadastrou, e
          é o mesmo código que a tela do Nasajon mostra (por exemplo{' '}
          <strong>OFIE25 — Oficina Externa - Carro</strong>).
        </p>
        <p>
          No filtro dá para marcar <strong>quantos centros quiser</strong>. Nada marcado significa{' '}
          <strong>todos</strong> — o painel abre assim, mostrando a empresa inteira. Vale lembrar
          que boa parte das requisições é de Marketing, RH, TI e Manutenção Predial: são compras da
          empresa, não da oficina.
        </p>
        <p>
          <strong>O painel troca de cards conforme o que está marcado.</strong> Quando todos os
          centros escolhidos são de oficina, existem <strong>Requisições por oficina</strong> e{' '}
          <strong>Requisições por placa</strong>, um em cima do outro — os dois falam de peça e de
          veículo (e, na aba Pedidos, <strong>Entrega no prazo</strong> segue a mesma regra). Quando
          nenhum deles é, esses cards saem, porque ali não respondem
          nada: a oficina aparece em pouquíssimas requisições e a placa que às vezes surge no texto
          é leitura equivocada. No lugar deles entram <strong>Requisições por solicitante</strong> e{' '}
          <strong>Itens mais requisitados</strong>.
        </p>
        <p>
          O card <strong>Requisições por centro de custo</strong> existe sempre, e é ele que mostra
          que ninguém fica de fora: <strong>a soma das linhas é o total do período</strong>.
        </p>
        <p>
          Requisição cujo local de uso <strong>não tem centro de custo</strong> — ou que foi gravada
          sem local de uso — aparece na linha{' '}
          <strong>&quot;Sem centro de custo&quot;</strong>. Ela não é descartada nem escondida: é o
          estado real daquele cadastro.
        </p>
      </>
    ),
  },
  {
    titulo: 'Requisições por centro de custo',
    conteudo: (
      <>
        <p>
          Uma linha por <strong>centro de custo</strong>, com o código do Nasajon ao lado. É o que
          separa Oficina Interna Carro, Oficina Externa Carro, Oficina Externa Moto, Vidro Premium,
          Marketing, TI e os demais — coisas que, no recorte antigo por setor, ficavam todas no
          mesmo balde.
        </p>
        <p>
          A soma das linhas é o <strong>total de requisições do período</strong>. É a forma de
          conferir, na própria tela, que o filtro não esconde nada: cada requisição pertence a
          exatamente uma linha.
        </p>
        <p>
          <strong>&quot;Sem centro de custo&quot;</strong> reúne as requisições cujo local de uso
          não carrega centro de custo no Nasajon e as que foram gravadas sem local de uso. O clique
          nela abre a lista, como em qualquer outra linha.
        </p>
      </>
    ),
  },
  {
    titulo: 'Solicitantes e itens, fora da oficina',
    conteudo: (
      <>
        <p>
          <strong>Requisições por solicitante</strong> mostra quem pediu e de qual setor de estoque. O nome vem
          do cadastro de usuários do Nasajon, o mesmo de onde sai o nome do comprador — quem não
          estiver lá aparece como não identificado, e o card diz em quantas requisições o
          solicitante foi encontrado.
        </p>
        <p>
          <strong>Itens mais requisitados</strong> agrupa pelo texto da descrição do item, sem
          diferenciar maiúsculas nem espaços a mais. <strong>Não há valor</strong>: a requisição no
          Nasajon não tem preço — ele nasce no pedido de compra, depois da cotação. O card mostra os
          primeiros itens e informa quantos existem no total, para ninguém ler a lista como se fosse
          completa.
        </p>
        <p>
          Os itens só aparecem para as requisições já sincronizadas com as linhas delas; o card
          informa quantas são.
        </p>
      </>
    ),
  },
  {
    titulo: 'O nome da oficina',
    conteudo: (
      <>
        <p>
          A oficina de destino não é um campo do ERP: ela aparece dentro do texto da requisição, e
          nem toda requisição a informa. Por isso o bloco mostra em quantas requisições ela foi
          identificada. Quando a mesma oficina aparece escrita de formas diferentes, um administrador
          pode unificá-las em <strong>Administração do Sistema</strong>.
        </p>
        <p>
          Este card só aparece quando o recorte inclui algum <strong>centro de custo de
          oficina</strong>. Marcando apenas centros como Marketing ou TI ele sai: aquela compra não
          vai para oficina nenhuma.
        </p>
      </>
    ),
  },
  {
    titulo: 'A placa do veículo',
    conteudo: (
      <>
        <p>
          A placa também não é um campo do ERP: ela vem do <strong>texto</strong> da requisição, e
          quem preenche escreve de formas diferentes — às vezes com a palavra &quot;placa&quot; na
          frente, às vezes solta no meio da frase. O sistema lê todas essas formas, e o card
          <strong> Requisições por placa</strong> mostra em quantas requisições ela foi encontrada.
        </p>
        <p>
          Requisição sem placa não é erro: boa parte das compras não é para um veículo específico
          (insumos, material de escritório, manutenção predial).
        </p>
        <p>
          Como o card de oficinas, este só aparece quando o recorte inclui algum{' '}
          <strong>centro de custo de oficina</strong>. Fora deles, as placas que surgem no texto são
          leitura equivocada de códigos, não veículos.
        </p>
      </>
    ),
  },
  {
    titulo: 'A aba Pedidos, e de onde vêm comprador, placa e oficina',
    conteudo: (
      <>
        <p>
          O pedido de compra, no ERP, <strong>não tem</strong> comprador, placa nem oficina — ele
          guarda fornecedor, valor, situação e datas. Os três aparecem aqui porque o sistema segue o
          caminho do pedido até a <strong>requisição que o originou</strong>, pela negociação de
          compra.
        </p>
        <p>
          Por isso existem pedidos sem esses campos: são <strong>compras feitas direto</strong>, sem
          requisição, ou pedidos cuja negociação ainda não foi trazida do Nasajon. Cada card mostra
          quantos são, abaixo da tabela, em <strong>Sem requisição de origem</strong> — e o número
          também abre a lista.
        </p>
        <p>
          A aba tem os mesmos cards da aba Requisições, voltados para pedidos:{' '}
          <strong>Pedidos por comprador</strong>, <strong>por centro de custo</strong>,{' '}
          <strong>por oficina</strong>, <strong>por placa</strong> e{' '}
          <strong>por solicitante</strong>. Todos vêm da requisição de origem, e todos contam{' '}
          <strong>pedidos</strong>: um veículo que teve três pedidos conta três vezes na placa dele.
          As linhas da tabela mais as linhas que ficam de fora (sem origem, ou sem o dado) somam o
          total do período.
        </p>
        <p>
          Os cards não mostram abertos nem cancelados por linha: a situação de cada pedido aparece na
          lista que o clique numa linha abre, na coluna <strong>Situação</strong>. Os cancelados
          continuam fora da contagem dos cards.
        </p>
        <p>
          <strong>Pedidos por oficina</strong> conta só as compras de peça, como o card de oficinas
          da aba Requisições: um pedido de vidro ou de reforma não tem oficina de destino. O asterisco marca
          a oficina que inclui pedidos de requisições fora das classificações de peça, mas que
          informam a oficina no texto.
        </p>
        <p>
          Quando o recorte não inclui nenhum <strong>centro de custo de oficina</strong>, os cards
          por oficina e por placa não aparecem, e as duas colunas somem da lista — pelo mesmo motivo
          da aba Requisições: compra de Marketing ou RH não tem veículo nem oficina de destino. Quando o
          recorte é só de oficina, o card por solicitante sai.
        </p>
        <p>
          O card <strong>Fornecedores</strong> fica nesta aba desde 22/09/2026: ele conta pedidos de
          compra, e não requisições, então o lugar dele é junto dos demais números de pedido.
        </p>
      </>
    ),
  },
  {
    titulo: 'Comentários em pedidos e requisições',
    conteudo: (
      <>
        <p>
          Ao abrir um pedido ou uma requisição (clicando no número dele em qualquer lista), o
          detalhe termina com os <strong>comentários</strong> da equipe sobre aquela compra, com
          respostas. Nas listas, o número ao lado do pedido ou da requisição diz quantos comentários
          ela tem.
        </p>
        <p>
          Num <strong>pedido</strong>, as conversas da <strong>requisição que o originou</strong>{' '}
          aparecem junto, marcadas com <strong>Da requisição</strong>. Quem comenta no pedido grava
          no pedido.
        </p>
        <p>
          Comentário <strong>não pode ser editado nem apagado</strong>. Para chamar alguém, escreva{' '}
          <strong>@</strong> e escolha a pessoa na lista: ela recebe um aviso no sino (canto superior
          direito, em todas as telas),
          mesmo sem participar da conversa. Quem já comentou naquele item é avisado de comentário
          novo, e quem escreveu um comentário é avisado da resposta.
        </p>
        <p>
          O aviso traz o link <strong>Abrir pedido</strong> (ou <strong>Abrir requisição</strong>),
          que abre o item direto em outra aba.
        </p>
        <p>
          Nos cards da aba Pedidos, o número cinza ao lado do nome de cada linha diz{' '}
          <strong>quantos pedidos daquele grupo têm comentário</strong>. Sem número, nenhum pedido
          do grupo foi comentado.
        </p>
        <p>
          Quem pode escrever e responder é definido na Administração do Sistema, em Compras.
        </p>
      </>
    ),
  },
  {
    titulo: 'As situações de um pedido',
    conteudo: (
      <>
        <p>
          A área de Compras acompanha o pedido por cinco situações: <strong>aberto</strong>,{' '}
          <strong>aguardando documento</strong>, <strong>parcialmente liquidado</strong>,{' '}
          <strong>liquidado</strong> e <strong>cancelado</strong>. São as mesmas que o Nasajon mostra
          na tela dele, e é delas que saem o <strong>Fluxo de pedidos</strong>, o filtro de status e
          a coluna <strong>Situação</strong> da lista de pedidos.
        </p>
        <p>
          Logo depois de uma atualização do sistema, pode aparecer a coluna{' '}
          <strong>Situação não sincronizada</strong>: são pedidos que o sistema ainda não releu do
          Nasajon com a situação nova. Ela some sozinha depois da próxima leitura completa.
        </p>
        <p>
          O <strong>Total de pedidos</strong> conta todos os pedidos emitidos no período, menos os
          cancelados. Os cancelados aparecem à parte e não entram no total, no valor nem nos itens.
        </p>
        <p>
          <strong>Dias em aberto</strong> conta os dias desde a emissão do pedido até hoje, só dos
          pedidos que ainda estão abertos.
        </p>
        <p>
          <strong>Itens</strong> só existem no sistema para os pedidos cujos itens já foram trazidos
          do Nasajon. O indicador diz em quantos pedidos isso já aconteceu.
        </p>
      </>
    ),
  },
  {
    titulo: 'Por que a aba Pedidos não tem "aguardando atendimento" nem "itens mais pedidos"',
    conteudo: (
      <>
        <p>
          <strong>Aguardando atendimento</strong>, <strong>em atendimento</strong>,{' '}
          <strong>em cotação</strong> e <strong>atendida</strong> são etapas da{' '}
          <strong>requisição</strong>, antes de o pedido existir. Quando o pedido nasce, a compra
          já foi feita — então essas etapas continuam na aba Requisições, e aqui fica o que o pedido tem.
        </p>
        <p>
          <strong>Itens mais pedidos</strong> não é possível: o item do pedido chega do Nasajon só
          com o identificador interno do produto, sem a descrição, e o sistema não tem o cadastro de
          produtos para traduzi-lo.
        </p>
      </>
    ),
  },
  {
    titulo: 'Por que a aba Pedidos não diz se a peça chegou',
    conteudo: (
      <>
        <p>
          Porque o dado não permite afirmar isso. O ERP <strong>não liga</strong> a nota fiscal de
          entrada ao pedido que a originou, e a peça que vai para oficina externa é avisada em grupo
          de WhatsApp, sem passar pelo estoque. Qualquer indicador de &quot;chegou no prazo&quot; por
          pedido seria um número sem fonte.
        </p>
        <p>
          O que existe é a <strong>previsão registrada no pedido</strong>, e a lista mostra só essa
          data. A aba não marca pedido como &quot;vencido&quot; ou &quot;atrasado&quot;: sem saber
          se a peça chegou, essa marca acusaria o fornecedor sem prova. O indicador volta quando o
          Nasajon passar a informar de qual pedido veio cada nota fiscal de entrada.
        </p>
      </>
    ),
  },
  {
    titulo: 'Por que não há tempo de atendimento nesta tela',
    conteudo: (
      <>
        <p>
          <strong>Tempo de atendimento</strong> seria a mediana de dias entre a requisição ficar
          pendente e virar compra. O Nasajon <strong>não guarda</strong> essa informação — ele diz
          que a requisição <em>está</em> em cotação, nunca <em>desde quando</em>.
        </p>
        <p>
          O sistema chegou a construir o número anotando cada mudança de situação que observava nas
          leituras do ERP. O problema é a dependência que isso cria: o número passa a valer{' '}
          <strong>só enquanto a sincronização estiver funcionando</strong>. Se ela ficar parada, a
          mudança não deixa de ser contada — ela é contada tarde, e o tempo aparece{' '}
          <strong>maior do que realmente foi</strong>, sem nada na tela indicando o problema.
        </p>
        <p>
          Por isso o indicador saiu do painel. Pedimos à Nasajon, em 22/09/2026, o histórico de
          mudanças de situação; enquanto ele não vier, preferimos não mostrar número nenhum a
          mostrar um que pode estar errado por uma falha nossa.
        </p>
        <p>
          Para o indicador valer desde o início da operação, esse histórico precisa ser{' '}
          <strong>consultável para trás</strong>. Se a Nasajon passar a registrar a data apenas de
          agora em diante, o tempo só poderá ser medido a partir daí — e a tela dirá isso.
        </p>
      </>
    ),
  },
  {
    titulo: 'Clique em qualquer número para ver quais requisições ele conta',
    conteudo: (
      <>
        <p>
          Todos os indicadores do topo são clicáveis, menos <strong>Total de itens</strong>. O clique abre a lista das requisições que formam aquele número, com a data de
          cadastro, a situação, a placa, a oficina de destino e a quantidade de itens; o número da
          requisição abre o detalhe, com comprador, solicitante e a justificativa inteira.
        </p>
        <p>
          <strong>Total de requisições</strong> conta tudo o que foi cadastrado no período, em
          qualquer situação — inclusive as canceladas. <strong>Total de itens</strong>, ao lado,
          soma os itens dessas mesmas requisições.
        </p>
        <p>
          A lista que <strong>Tempo na fila de Compras</strong> abre tem uma coluna a mais,{' '}
          <strong>Dias na fila</strong>: há quantos dias cada requisição foi cadastrada no Nasajon,
          pela mesma conta do indicador. Ela só existe nessa lista porque ali toda linha ainda está
          esperando — em uma requisição já atendida, &quot;dias desde o cadastro&quot; seria a idade
          do registro, não espera.
        </p>
        <p>
          A lista respeita <strong>os mesmos filtros</strong> que estão aplicados no painel: mudando
          o centro de custo ou o período, o número e a lista mudam juntos.
        </p>
        <p>
          Na aba <strong>Pedidos</strong> vale o mesmo: os indicadores, as colunas do fluxo e as
          linhas dos cards abrem a lista dos pedidos que formam aquele número.
        </p>
        <p>
          Um clique merece explicação. <strong>Tempo na fila de Compras</strong> não é uma contagem, é
          uma mediana em dias — o clique ali abre a fila ordenada <strong>da mais antiga para a
          mais recente</strong>, que é o conjunto que aquele número resume. Ela também segue o
          período: com um mês escolhido, é o tempo das requisições{' '}
          <strong>cadastradas naquele mês</strong>, não o da fila inteira.
        </p>
        <p>
          O que <strong>Tempo na fila de Compras</strong> mede, por extenso: quantos dias a
          requisição ficou na fila, contados do cadastro <strong>no Nasajon</strong>. Se ela ainda
          está na fila — em cotação, em negociação ou pendente já aprovada —, conta até{' '}
          <strong>hoje</strong>; se já virou pedido, conta até a <strong>emissão do primeiro pedido
          de compra</strong> gerado a partir dela. As duas entram no mesmo número, e a linha abaixo
          dele diz quantas são de cada. É o tempo até a <strong>compra sair</strong>, e não até a
          peça chegar. Pedidos cancelados e requisições ainda não aprovadas não entram. O card diz
          a partir de que data o sistema consegue ligar cada pedido à sua requisição.
        </p>
        <p>
          <strong>Tempo na fila por comprador</strong> é esse mesmo número, separado por pessoa, com
          quantas requisições estão em aberto e quantas já viraram pedido ao lado. As requisições
          sem comprador ficam fora das barras e são contadas à parte.
        </p>
        <p>
          O número que aparece em <strong>Fora do período</strong> também abre: são as requisições
          antigas que seguem em aberto no ERP e que, de propósito, não entram na conta dos cards.
        </p>
        <p>
          No gráfico de <strong>Evolução mensal</strong>, clicar numa coluna abre os{' '}
          <strong>pedidos de compra</strong> daquele mês — outra coisa que a requisição: um pedido
          nasce depois, já com fornecedor e valor. Ali os pedidos <strong>cancelados</strong> não
          aparecem, pelo mesmo motivo que não entram na altura da coluna.
        </p>
        <p>
          Os blocos de baixo também abrem no clique. Em <strong>Carga por comprador</strong>, o
          nome abre as requisições daquela pessoa. Em <strong>Tempo na fila por comprador</strong>,
          o nome abre as requisições dela — em aberto e já convertidas em pedido, da mais antiga para a mais
          recente, com as colunas <strong>Dias na fila</strong> e <strong>Fila</strong>. Em <strong>Fornecedores</strong>, o nome abre os{' '}
          <strong>pedidos de compra</strong> que somam aquele valor, com a placa e a oficina de
          destino quando o pedido veio de uma requisição. Em <strong>Requisições por oficina</strong>{' '}
          e <strong>Requisições por placa</strong>, a primeira coluna abre a lista daquela linha —
          inclusive a linha &quot;Sem oficina informada&quot;, que reúne as requisições cujo texto
          não diz para onde a peça vai. Em todas elas a lista traz a <strong>placa</strong> e a{' '}
          <strong>oficina</strong> de cada requisição.
        </p>
        <p>
          <strong>Requisições por oficina</strong> tem um filtro próprio para separar as duas
          leituras: <em>somente oficinas identificadas</em> mostra o ranking de para onde as peças
          vão; <em>somente oficinas não identificadas</em> mostra o que falta preencher, que é a
          lista para corrigir na origem. Ele recorta <strong>só este card</strong> — os demais
          blocos continuam com o período e os filtros do topo. A frase de cobertura acima da tabela
          continua falando do período inteiro; quando há filtro, ela diz também quantas linhas
          estão sendo mostradas.
        </p>
        <p>
          Na aba Pedidos, em <strong>Entrega no prazo</strong>, o nome do fornecedor abre os
          pedidos medidos dele: o número do pedido, as notas que o atenderam, a entrada da última
          nota, a previsão do pedido e a diferença entre as duas. Ali aparecem{' '}
          <strong>apenas os pedidos que puderam ser medidos</strong> — os que ficaram de fora estão
          contados logo abaixo do indicador.
        </p>
        <p>
          As tabelas de <strong>Requisições por oficina</strong>, <strong>Requisições por placa</strong>{' '}
          e <strong>Últimas requisições</strong> são paginadas. A primeira pagina a lista que já está
          na tela; as outras duas buscam cada página no servidor, porque o período pode ter
          centenas de placas e milhares de requisições. Por isso a busca por placa procura em{' '}
          <strong>todas</strong> as placas do período, não só nas que estão aparecendo.
        </p>
      </>
    ),
  },
  {
    titulo: 'O filtro de status dentro de um card',
    conteudo: (
      <>
        <p>
          <strong>Carga por comprador</strong> e <strong>Requisições por placa</strong> têm um filtro
          de status próprio, dentro do card. Ele responde a perguntas como &quot;quanto cada
          comprador tem em cotação agora&quot; ou &quot;quais veículos têm peça com pedido
          gerado&quot;.
        </p>
        <p>
          Esse filtro vale <strong>só para o card onde ele está</strong>: o resto do painel — os
          indicadores do topo, a evolução mensal, os fornecedores — continua
          mostrando o período inteiro. É diferente do botão <strong>Filtros</strong> lá em cima, que
          refaz a tela toda.
        </p>
        <p>
          Com um status escolhido, o clique numa linha abre a lista já recortada por ele — a lista e
          o número do card falam sempre do mesmo conjunto.
        </p>
      </>
    ),
  },
  {
    titulo: 'Os filtros dentro de uma lista',
    conteudo: (
      <>
        <p>
          Toda lista que abre ao clicar num número tem o botão <strong>Filtros</strong> no alto:
          busca pelo número ou pela placa, situação, placa, oficina, comprador, solicitante,
          fornecedor e período. A <strong>busca</strong> procura em tudo (número, placa, fornecedor,
          comprador, oficina); com várias palavras, todas precisam aparecer, em qualquer ordem, e
          acento e maiúsculas não importam.
        </p>
        <p>
          Esses filtros <strong>só estreitam a lista</strong>: ela continua sendo o recorte do
          número clicado, e o número do card não muda. Por isso a linha abaixo do título avisa
          quando a lista está filtrada, e o botão mostra quantos filtros estão valendo.
        </p>
        <p>
          Placa, oficina, comprador e solicitante de um pedido vêm da requisição que o originou: o
          pedido sem requisição de origem sai da lista quando um deles é usado.
        </p>
      </>
    ),
  },
  {
    titulo: 'A aba Sincronização',
    conteudo: (
      <>
        <p>
          Mostra <strong>quando cada parte do painel foi lida</strong> do Nasajon pela última vez,
          quanto a leitura levou e se ela trouxe tudo. É onde conferir o quão fresco é o número que
          você está vendo.
        </p>
        <p>
          Quando uma sincronização está acontecendo, a aba mostra em que etapa ela está e uma
          <strong> estimativa</strong> do que falta — calculada pelo ritmo das etapas já concluídas,
          e por isso aproximada. Enquanto houver só uma etapa concluída, o sistema diz que ainda não
          tem base para estimar, em vez de arriscar um número.
        </p>
        <p>
          <strong>Parcial</strong> é um desfecho próprio: a leitura terminou, mas não trouxe tudo
          (uma página do ERP falhou, ou um registro foi recusado). O painel continua correto no que
          mostra, só está incompleto — e a observação diz o motivo.
        </p>
        <p>
          É também onde fica a <strong>carga histórica dos itens de pedido</strong>. A leitura de
          hora em hora traz os itens dos pedidos dos últimos três dias; a carga busca os doze meses
          anteriores, leva cerca de três horas e precisa ser feita <strong>uma vez só</strong>, logo
          depois de o sistema ser atualizado. Sem ela, a aba Pedidos conta zero itens no histórico.
        </p>
        <p>
          O selo de cada parte compara a idade do dado com o <strong>ritmo que ela promete</strong>,
          e não com um prazo único: cada parte é lida numa frequência diferente. Por isso
          &quot;há 4 horas&quot; aparece como <strong>Atrasando</strong> na Base completa, que é lida
          a cada 30 minutos, e continua <strong>Em dia</strong> nos Cadastros, que são lidos uma vez
          por semana. <strong>Em dia</strong> cobre até três leituras perdidas; <strong>Atrasando</strong>,
          até oito; acima disso, <strong>Atrasado</strong>.
        </p>
        <p>
          <strong>Fora do horário</strong> não é atraso: é a leitura parada porque estamos fora do
          expediente. Numa segunda de manhã, as partes lidas com frequência aparecem assim até as
          8h — o dado está velho, mas por decisão, e volta a ser atualizado na abertura. O cartão
          diz em que horário aquela parte roda.
        </p>
        <p>
          Uma leitura <strong>interrompida</strong> não é defeito e aparece como nota discreta: é o
          que acontece quando o sistema é reiniciado no meio de uma leitura longa, e a parte volta
          sozinha para a fila na próxima vez. O aviso em destaque fica para quando o ERP{' '}
          <strong>recusou</strong> a consulta — aí pode ser preciso acionar a TI.
        </p>
        <p>
          <strong>Pausado</strong> é outra coisa: alguém da Administração do Sistema interrompeu as
          consultas ao Nasajon de propósito. Enquanto durar, nenhuma parte é lida, os números do
          painel não mudam e uma faixa no topo diz desde quando e por quem. Não é falha, e a única
          saída é retomar em Administração do Sistema, na tela de Compras.
        </p>
        <p>
          O painel também pode estar em ritmo de <strong>uma leitura por dia</strong>, usado em
          períodos de pouco movimento para reduzir o número de consultas ao Nasajon. Nesse ritmo os
          números não mudam durante o expediente — a leitura acontece no fim do dia, no horário que
          a faixa e os cartões informam —, e o &quot;até 3 minutos&quot; descrito acima deixa de
          valer. Cada cartão passa a ser comparado com esse ritmo, e não com a frequência de sempre.
        </p>
      </>
    ),
  },
];

type Aba = 'PAINEL' | 'PEDIDOS' | 'SINCRONIZACAO';

/*
 * ⚠️ Aba PEDIDOS separada do Painel (at-109), e não mais cards nele: o Painel já tem KPIs, funil,
 * evolução, quatro cards e duas tabelas. Os indicadores de pedido são uma leitura própria — e
 * empilhá-los ali empurraria para baixo da dobra justamente o que a pessoa veio ver (a mesma
 * decisão do Radar do Relacionamento, 05/09/2026).
 */
const ABAS: Array<{ id: Aba; rotulo: string }> = [
  // at-122: a aba se chama "Requisições" (pedido do Victor, 07/10/2026); o id fica PAINEL.
  { id: 'PAINEL', rotulo: 'Requisições' },
  { id: 'PEDIDOS', rotulo: 'Pedidos' },
  { id: 'SINCRONIZACAO', rotulo: 'Sincronização' },
];

interface Props {
  sidebarRef?: React.RefObject<HTMLElement>;
}

export default function DashboardComprasPage({ sidebarRef }: Props) {
  // at-112: sem recorte inicial. O eixo deixou de ser um menu de três opções e passou a ser o
  // centro de custo — abrir filtrado em um deles esconderia do usuário o que ele nem sabe que
  // existe, e a lista de 26 não tem um "default óbvio" como "Peças" era.
  const [filtros, setFiltros] = useState<FiltrosCompras>({});
  const [filtroAberto, setFiltroAberto] = useState(false);
  const [ajudaAberta, setAjudaAberta] = useState(false);
  // at-129: o pedido/requisição aberto pelo sino (aviso de comentário ou de menção).
  const [itemDaNotificacao, setItemDaNotificacao] = useState<{ tipo: 'PEDIDO' | 'REQUISICAO'; id: string } | null>(
    null,
  );
  /*
   * transversal/at-90 — o link direto (`?pedido=<id>` / `?requisicao=<id>`), que o "Abrir pedido"
   * do aviso de menção abre em outra aba. Lido uma vez e retirado da URL: sem isso, o F5 reabriria
   * o pedido e o endereço compartilhado depois carregaria um modal que ninguém pediu.
   */
  const [parametrosDaUrl, setParametrosDaUrl] = useSearchParams();
  useEffect(() => {
    const pedido = parametrosDaUrl.get('pedido');
    const requisicao = parametrosDaUrl.get('requisicao');
    if (!pedido && !requisicao) return;
    setItemDaNotificacao(pedido ? { tipo: 'PEDIDO', id: pedido } : { tipo: 'REQUISICAO', id: requisicao! });
    setParametrosDaUrl({}, { replace: true });
  }, [parametrosDaUrl, setParametrosDaUrl]);
  const [abaAtiva, setAbaAtiva] = useState<Aba>('PAINEL');
  // Qual card está aberto em detalhamento. `null` = nenhum; a montagem é condicional, para o modal
  // recomeçar do zero a cada abertura (padrão do projeto — rascunho que sobrevive ao fechar foi o
  // defeito do modal de filtros da at-61).
  const [recorteAberto, setRecorteAberto] = useState<RecorteAberto | null>(null);
  // O mês aberto pelo clique numa coluna do gráfico. Modal próprio porque a entidade é outra —
  // pedido de compra, não requisição: colunas, filtros e definição de "conta" são diferentes.
  const [mesAberto, setMesAberto] = useState<string | null>(null);
  /*
   * at-110 — os detalhamentos que a entrega acrescentou. Cada um é um estado próprio porque a
   * ENTIDADE é diferente: requisição (comprador, oficina, placa), pedido de compra (fornecedor) e
   * entrega medida (pontualidade). Um modal só, com colunas variáveis, esconderia que os três
   * números vêm de fontes diferentes e têm coberturas diferentes.
   */
  const [fornecedorAberto, setFornecedorAberto] = useState<FornecedorAberto | null>(null);
  /*
   * at-115 — as PEÇAS de um comprador. Modal próprio pelo mesmo motivo dos irmãos acima: a
   * entidade é outra (item de requisição, não requisição), e as colunas e a contagem são de outra
   * grandeza — "5 códigos de peça" não é "5 requisições".
   */
  const [itensAbertos, setItensAbertos] = useState<ItensAbertos | null>(null);
  /*
   * at-121 — a lista de PEDIDOS de um número da aba Pedidos (KPI, coluna do fluxo, linha de card).
   * Modal próprio, e não o de requisições, porque a entidade é outra: colunas e filtros diferentes.
   */
  const [recortePedidosAberto, setRecortePedidosAberto] = useState<PedidosDoRecorteAberto | null>(
    null,
  );
  /*
   * Os filtros de status dos dois cards. Ficam na PÁGINA, e não dentro de cada bloco, porque quem
   * dispara a consulta é o hook — estado no bloco obrigaria cada um a ter carga própria, e aí a
   * tela teria duas fontes de verdade sobre o que está carregado.
   */
  // at-112: multi-seleção. Lista vazia = todos os status (o padrão).
  const [statusCompradores, setStatusCompradores] = useState<string[]>([]);
  const [statusPlacas, setStatusPlacasCru] = useState<string[]>([]);
  // at-118: a busca e a página do card de placas vão ao servidor (BUG-014). Mudar o status ou a
  // busca volta para a página 1 — a página 3 do recorte anterior viraria lista vazia sem motivo.
  const [buscaPlaca, setBuscaPlacaCru] = useState('');
  const [paginaPlacas, setPaginaPlacas] = useState(1);
  const setStatusPlacas = (s: string[]) => {
    setStatusPlacasCru(s);
    setPaginaPlacas(1);
  };
  const setBuscaPlaca = (b: string) => {
    setBuscaPlacaCru(b);
    setPaginaPlacas(1);
  };
  // at-117: um valor só — as três opções do card de oficinas são mutuamente exclusivas.
  const [identificacaoOficina, setIdentificacaoOficina] = useState<IdentificacaoDeOficina>('todas');

  const {
    resumo,
    evolucao,
    compradores,
    filaPorComprador,
    fornecedores,
    oficinas,
    requisicoes,
    placas,
    porCentroCusto,
    centros,
    solicitantes,
    itens,
    status,
    recarregar,
  } = useComprasDashboard(filtros, {
    compradores: statusCompradores,
    placas: statusPlacas,
    buscaPlaca,
    paginaPlacas,
    oficinas: identificacaoOficina,
  });

  /**
   * O que o cabeçalho diz do recorte (at-112).
   *
   * ⚠️ Com um centro só, mostra o NOME dele: é a informação que o usuário procura ao voltar à
   * tela, e "1 centro de custo" o obrigaria a abrir o modal para lembrar qual. A partir de dois, a
   * contagem é o que cabe na faixa.
   */
  const escolhidos = filtros.centrosCusto ?? [];
  const rotuloDoRecorte =
    escolhidos.length === 0
      ? 'Todos os centros de custo'
      : escolhidos.length === 1
        ? (centros ?? []).find((c) => c.pk === escolhidos[0])?.descricao ?? 'Centro de custo'
        : `${escolhidos.length} centros de custo`;
  /*
   * 🔴 A MESMA função que o hook usa para decidir o que buscar (`blocosDoModo`).
   *
   * Duas cópias da regra produziriam um dos dois defeitos: bloco montado sem carga (esqueleto
   * eterno) ou consulta paga para desenhar nada. É a mesma razão de `chaveDoMes`/`janelaDoMes`
   * viverem juntas no backend.
   */
  const blocos = blocosDoModo(filtros.centrosCusto, centros ?? undefined);

  return (
    /*
     * ⚠️ O casco é o MESMO das outras páginas: `ConteDo recepcao-conte-do do-pagina`. Usar só
     * `do-pagina` (como a primeira versão fazia) deixa a tela sem o recuo e a largura que todas as
     * demais têm — foi uma das diferenças visíveis na comparação com a Recepção.
     */
    <div className="ConteDo recepcao-conte-do do-pagina">
      <div className="recepcao-header-row">
        {/*
          ⚠️ Sem `fontSize` inline: os 20px vieram de um componente de ABA (`VisaoGeralOficinaTab`),
          onde o título é interno ao painel. Título de PÁGINA usa o tamanho padrão do
          `recepcao-title`, como Recepção e Dashboard Oficina.
        */}
        <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Dashboard de Compras
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setAjudaAberta(true)}
          >
            ?
          </button>
        </div>
        {/* transversal/at-90: o sino desta tela saiu — o do topo abre o item pelo link direto. */}
      </div>

      {/* Os controles são uma FAIXA PRÓPRIA abaixo do cabeçalho, não um bloco à direita dele. */}
      <div className="do-controles">
        <button type="button" className="do-btn-filtros" onClick={() => setFiltroAberto(true)}>
          Filtros
        </button>
        <span className="do-periodo">
          {rotuloDoRecorte}
          {/*
            ⚠️ `formatarDataPura`, não `new Date`: a janela vem como data pura do servidor, e
            `new Date('2026-08-23')` é meia-noite UTC — no fuso da oficina imprimiria o dia
            anterior. Ver o cabeçalho de `formatarData.ts`.
          */}
          {resumo.dados
            ? ` · ${formatarDataPura(resumo.dados.janela.de)} a ${formatarDataPura(resumo.dados.janela.ate)}`
            : ''}
        </span>
        <CarimboSync status={status} />
      </div>

      <div className="recepcao-tabs">
        {ABAS.map((aba) => (
          <button
            key={aba.id}
            type="button"
            className={`recepcao-tab ${abaAtiva === aba.id ? 'active' : ''}`}
            onClick={() => setAbaAtiva(aba.id)}
          >
            {aba.rotulo}
          </button>
        ))}
      </div>

      {/*
        As abas são MONTADAS E DESMONTADAS na troca (padrão do Dashboard Oficina), e não escondidas
        com display:none — a aba que entra sempre recarrega com o estado em vigor.
      */}
      {abaAtiva === 'SINCRONIZACAO' ? (
        <PainelSincronizacao status={status} recarregarStatus={recarregar} />
      ) : abaAtiva === 'PEDIDOS' ? (
        <>
          {/*
            🔴 A faixa de pausa/ritmo TAMBÉM aqui (corrigido em 21/09/2026). Ela estava só no
            Painel, e no primeiro teste manual a aba Pedidos mostrou "0 de 717" sem dizer que o
            sync estava pausado desde a véspera — a explicação existia, mas na aba errada.
          */}
          <FaixaEstadoDaSincronizacao
            sincronizacao={status.dados?.sincronizacao}
            ultimoDadoEm={status.dados?.camadas.QUENTE?.ultimoSucesso}
          />
          <AbaPedidos
            filtros={filtros}
            centros={centros ?? undefined}
            fornecedores={fornecedores}
            aoAbrirFornecedor={setFornecedorAberto}
            evolucao={evolucao}
            aoAbrirMes={setMesAberto}
            aoAbrirRecorte={setRecortePedidosAberto}
          />
        </>
      ) : (
      <>
      {/*
        A integração pode não estar configurada no ambiente (as envs do Nasajon são definidas
        separadamente do deploy). Nesse caso a tela EXPLICA, em vez de exibir zeros que seriam
        lidos como a realidade da oficina.
      */}
      {status.dados && !status.dados.configurado ? (
        <p className="erro-de-tela">
          A integração com o Nasajon não está configurada neste ambiente, então o painel ainda não
          tem dados. Acione a TI.
        </p>
      ) : null}

      {/*
        at-108 — a pausa e o ritmo diário são a explicação de por que os números não estão mudando,
        e ela tem de aparecer na aba que a pessoa está olhando. Componente compartilhado com a aba
        Sincronização: duas redações do mesmo estado acabariam dizendo coisas diferentes.
      */}
      <FaixaEstadoDaSincronizacao
        sincronizacao={status.dados?.sincronizacao}
        ultimoDadoEm={status.dados?.camadas.QUENTE?.ultimoSucesso}
      />

      {/*
        Sem o cadastro de locais sincronizado, o sistema não sabe separar os setores — e mostra a
        empresa inteira. Dizer isso é obrigatório: quem escolheu "Peças" e vê os números de todo
        mundo concluiria que a oficina compra muito mais do que compra.
      */}
      {resumo.dados?.centroCustoNaoAplicado ? (
        <p className="aviso-de-tela">
          O filtro de centro de custo ainda não pôde ser aplicado: o cadastro de locais de uso não
          foi sincronizado. Os números abaixo são da empresa inteira, não apenas de{' '}
          {rotuloDoRecorte}.
        </p>
      ) : null}

      {/*
        at-124: o "Fluxo de compras" saiu — as etapas dele são cards desta fileira, e o
        "Fora do período" vem logo abaixo dela.
      */}
      <KpisCompras estado={resumo} aoDetalhar={setRecorteAberto} />

      <div className="do-grid">
        <CargaCompradores
          estado={compradores}
          situacoes={statusCompradores}
          onSituacoes={setStatusCompradores}
          /*
            at-115 — os outros dois números do card recortado também abrem (pedido do Victor).
            `aoDetalhar` serve o "Em aberto", que é o recorte `pendentes` do painel; `aoAbrirItens`
            abre as PEÇAS, que têm modal próprio porque não são requisições.
          */
          aoDetalhar={setRecorteAberto}
          aoAbrirItens={setItensAbertos}
          aoAbrirComprador={(c) =>
            setRecorteAberto({
              titulo: `Requisições de ${c.nome ?? 'comprador sem nome cadastrado'}`,
              // 🔴 Os MESMOS filtros que produziram a barra: o comprador da linha e o status do
              // card. Sem o status, a lista abriria com todas as requisições da pessoa enquanto a
              // barra conta só as do recorte — e os dois números não bateriam.
              filtrosExtras: {
                comprador: c.id,
                situacoes: statusCompradores.length ? statusCompradores : undefined,
              },
              legenda: statusCompradores.length ? rotuloDosStatus(statusCompradores) : undefined,
            })
          }
        />
        {/*
          at-119 — ao lado da carga, no MESMO grid, porque os dois respondem sobre a mesma pessoa:
          um diz quanto ela comprou, o outro há quanto tempo a fila dela está parada. É a métrica
          do KPI "Tempo na fila de Compras", por comprador — não o tempo de atendimento da at-116,
          que segue à espera da Nasajon. Em tela estreita o `auto-fit` empilha os dois.

          ⚠️ Fora de `blocosDoModo` de propósito: o card é sobre o comprador, não sobre peça, e a
          pergunta vale em qualquer centro de custo.
        */}
        <FilaPorCompradorBloco
          estado={filaPorComprador}
          aoAbrirComprador={(c) =>
            setRecorteAberto({
              // 🔴 O MESMO recorte do KPI (`pendentes`), recortado pela pessoa: é exatamente a
              // população que produziu a barra. Um recorte próprio divergiria dela.
              recorte: 'pendentes',
              titulo: `Fila de ${c.nome ?? 'comprador sem nome cadastrado'}`,
              filtrosExtras: { comprador: c.id },
              legenda: 'ainda sem pedido gerado, da mais antiga para a mais recente',
            })
          }
        />
      </div>

      {/*
        🔴 MONTAGEM CONDICIONAL, nunca `display: none`: bloco escondido com CSS continuaria pedindo
        a carga dele, e o que o modo "Outros setores" faz é justamente parar de perguntar por
        oficina e placa — que ali não respondem nada.

        ⚠️ "Entrega no prazo" saiu daqui na at-119 (pedido do Victor, 02/10/2026): ele fala de
        PEDIDO de compra (fornecedor, nota fiscal), e o lugar dele é a aba Pedidos, ao lado de
        Fornecedores e da Evolução mensal — a mesma mudança que a at-112 fez com aqueles dois.

        at-119 — oficina e placa UM EM CIMA DO OUTRO (pedido do Victor), cada um em largura cheia:
        lado a lado, as duas tabelas disputavam meia coluna e a de placa, com busca, paginação e
        o nome do comprador, saía espremida.
      */}
      {blocos.cardsDePeca ? (
        <>
          <div className="do-grid-largo">
            <Oficinas
              estado={oficinas}
              identificacao={identificacaoOficina}
              onIdentificacao={setIdentificacaoOficina}
              aoAbrirOficina={(oficina) =>
                setRecorteAberto({
                  titulo: `Requisições de ${oficina}`,
                  filtrosExtras: { oficina },
                })
              }
            />
          </div>

          {/* at-109: o agrupamento por veículo, irmão do de oficinas e com a mesma cobertura. */}
          <div className="do-grid-largo">
            <RequisicoesPorPlacaBloco
              estado={placas}
              situacoes={statusPlacas}
              onSituacoes={setStatusPlacas}
              busca={buscaPlaca}
              onBusca={setBuscaPlaca}
              pagina={paginaPlacas}
              onPagina={setPaginaPlacas}
              aoAbrirPlaca={(placa) =>
                setRecorteAberto({
                  titulo: `Requisições da placa ${placa}`,
                  filtrosExtras: {
                    placa,
                    situacoes: statusPlacas.length ? statusPlacas : undefined,
                  },
                  legenda: statusPlacas.length ? rotuloDosStatus(statusPlacas) : undefined,
                })
              }
            />
          </div>
        </>
      ) : null}

      {/*
        at-111 — os dois cards que respondem pelos outros setores. Em "Todos" só o de solicitantes
        entra: itens de peça são milhares de códigos distintos e achatariam o ranking.
      */}
      {blocos.solicitantes || blocos.itens ? (
        <div className={blocos.itens ? 'do-grid' : 'do-grid-largo'}>
          {blocos.solicitantes ? (
            <RequisicoesPorSolicitanteBloco
              estado={solicitantes}
              aoAbrirSolicitante={(pessoa) =>
                setRecorteAberto({
                  titulo: `Requisições de ${pessoa.nome ?? 'pessoa não identificada no Nasajon'}`,
                  filtrosExtras: { solicitante: pessoa.id },
                })
              }
            />
          ) : null}
          {blocos.itens ? <ItensMaisRequisitadosBloco estado={itens} /> : null}
        </div>
      ) : null}

      {/*
        at-111 — o card que existe nos três modos, e o único que PROVA na tela que os recortes de
        setor somam a base inteira. Largura cheia: em Todos ele tem cinco colunas.

        at-129 — desceu para logo acima de "Últimas requisições" (decisão do Victor, 22/10/2026):
        a ordem do fim da aba é Placa → Solicitante → Centro de custo, a mesma da aba Pedidos.
      */}
      <div className="do-grid-largo">
        <RequisicoesPorCentroCustoBloco
          estado={porCentroCusto}
          aoAbrirCentro={(centro) =>
            setRecorteAberto({
              titulo: `Requisições de ${centro.descricao}`,
              // 🔴 `SEM_CC` para a linha sem centro: omitir o parâmetro significa "todos os
              // centros", e a linha abriria a lista inteira em vez da dela.
              filtrosExtras: { centrosCusto: [centro.centroCusto ?? SEM_CENTRO_CUSTO] },
            })
          }
        />
      </div>

      <div className="do-grid-largo">
        <UltimasRequisicoesBloco estado={requisicoes} filtros={filtros} />
      </div>
      </>
      )}

      {/*
        Montagem condicional, não prop `isOpen`: o modal refaz a consulta e volta à página 1 a cada
        abertura, em vez de reabrir no estado do card anterior.
      */}
      {recorteAberto && (
        <RequisicoesDoRecorteModal
          aberto={recorteAberto}
          // 🔴 Os MESMOS filtros do painel. Sem eles a lista abriria sobre a base inteira enquanto
          // o card contou só o setor e o período escolhidos — e os dois números nunca bateriam.
          filtros={filtros}
          onFechar={() => setRecorteAberto(null)}
        />
      )}

      {/* at-129 — o item de um aviso do sino. O número vem dentro do próprio modal. */}
      {itemDaNotificacao?.tipo === 'PEDIDO' && (
        <DetalheDoPedidoModal
          pedidoId={itemDaNotificacao.id}
          numero={null}
          onFechar={() => setItemDaNotificacao(null)}
        />
      )}
      {itemDaNotificacao?.tipo === 'REQUISICAO' && (
        <DetalheDaRequisicaoModal
          requisicaoId={itemDaNotificacao.id}
          codigo={null}
          onFechar={() => setItemDaNotificacao(null)}
        />
      )}

      {/*
        at-115 — os filtros do painel também aqui: a coluna do gráfico passou a aplicá-los, e a
        lista tem de somar exatamente a coluna que a abriu.
      */}
      {mesAberto && (
        <PedidosDoMesModal
          mes={mesAberto}
          filtros={filtros}
          onFechar={() => setMesAberto(null)}
        />
      )}

      {/* at-115 — as peças do card recortado. Leva o status do card: foi com ele que o número saiu. */}
      {itensAbertos && (
        <ItensDoCompradorModal
          aberto={itensAbertos}
          filtros={filtros}
          situacoes={statusCompradores}
          onFechar={() => setItensAbertos(null)}
        />
      )}

      {/*
        at-110 — os dois detalhamentos novos. Recebem os filtros do painel porque os cards que os
        abrem também os respeitam: sem eles, a lista somaria um conjunto diferente do número
        clicado.
      */}
      {/*
        at-121 — os pedidos de um número da aba Pedidos. Recebe os filtros do painel porque o número
        clicado foi contado com eles: sem isso, a lista somaria outro conjunto.
      */}
      {recortePedidosAberto && (
        <PedidosDoRecorteModal
          aberto={recortePedidosAberto}
          filtros={filtros}
          onFechar={() => setRecortePedidosAberto(null)}
        />
      )}

      {fornecedorAberto && (
        <PedidosDoFornecedorModal
          aberto={fornecedorAberto}
          filtros={filtros}
          onFechar={() => setFornecedorAberto(null)}
        />
      )}

      {filtroAberto && (
        <FiltroComprasModal
          aplicados={filtros}
          sidebarRef={sidebarRef}
          onAplicar={(novos) => {
            setFiltros(novos);
            setFiltroAberto(false);
          }}
          onFechar={() => setFiltroAberto(false)}
        />
      )}

      <ComoFunciona
        isOpen={ajudaAberta}
        onClose={() => setAjudaAberta(false)}
        titulo="Como Funciona - Dashboard de Compras"
        secoes={helpSections}
      />
    </div>
  );
}
